import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import jobService from '../../services/jobService';
import { useAuth } from '../../context/AuthContext';
import './JobList.css';

const JobList = () => {
  const { isAuthenticated, user } = useAuth();
  const inDashboard = isAuthenticated && user?.role === 'candidate';
  const [searchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [jobTypes, setJobTypes] = useState([]);

  // Initialize from URL params
  const urlSearch = searchParams.get('search') || '';
  const urlLocation = searchParams.get('location') || '';

  const [searchInput, setSearchInput] = useState(urlSearch);
  const [locationInput, setLocationInput] = useState(urlLocation);
  const [filters, setFilters] = useState({
    search: urlSearch,
    jobType: '',
    workMode: '',
    location: urlLocation,
    page: 1,
    limit: 12
  });

  useEffect(() => {
    fetchJobTypes();
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [filters.search, filters.jobType, filters.workMode, filters.location, filters.page]);

  const fetchJobTypes = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/master/job-types`);
      setJobTypes(response.data.data || []);
    } catch (error) {
      console.error('Error fetching job types:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await jobService.getJobs(filters);
      setJobs(response.data);
      setPagination(response.pagination || {});
      setLoading(false);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to fetch jobs');
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
      page: 1
    });
  };

  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleLocationInputChange = (e) => {
    setLocationInput(e.target.value);
  };

  const handleLocationBlur = () => {
    if (locationInput !== filters.location) {
      setFilters({
        ...filters,
        location: locationInput,
        page: 1
      });
    }
  };

  const handleLocationKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setFilters({
        ...filters,
        location: locationInput,
        page: 1
      });
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters({
      ...filters,
      search: searchInput,
      page: 1
    });
  };

  const clearFilters = () => {
    setSearchInput('');
    setLocationInput('');
    setFilters({
      search: '',
      jobType: '',
      workMode: '',
      location: '',
      page: 1,
      limit: 12
    });
  };

  const handlePageChange = (newPage) => {
    setFilters({ ...filters, page: newPage });
  };

  const formatSalary = (job) => {
    if (!job.salary || !job.salary.min) return 'Not disclosed';
    const { min, max, currency, period } = job.salary;
    if (max) {
      return `${currency} ${min.toLocaleString()} - ${max.toLocaleString()} / ${period}`;
    }
    return `${currency} ${min.toLocaleString()} / ${period}`;
  };

  const formatLocation = (loc) => {
    if (!loc) return '';
    const city = typeof loc.city === 'object' ? loc.city?.name : loc.city;
    return [city, loc.state, loc.country, loc.address].filter(Boolean).join(', ');
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60
    };

    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      const interval = Math.floor(seconds / secondsInUnit);
      if (interval >= 1) {
        return `${interval} ${unit}${interval > 1 ? 's' : ''} ago`;
      }
    }
    return 'Just now';
  };

  if (loading) {
    return (
      <div className="public-job-list-loading">
        <div className="jl-spinner"></div>
        <p>Loading jobs...</p>
      </div>
    );
  }

  return (
    <div className={`public-job-list${inDashboard ? ' in-dashboard' : ''}`}>
      {/* Hero */}
      <div className="jl-hero">
        <p className='heading-top'>Find Your Next Opportunity</p>
        <h1 className="jl-hero-title">Find Your <span style={{color:'#5e9c20'}}>Dream Job</span></h1>
        <p className="jl-hero-sub">Explore {pagination.total || 0}+ opportunities from top companies</p>
        <form onSubmit={handleSearch} className="jl-search-bar">
          <div className="jl-search-input-wrap">
            <span className="material-symbols-outlined">search</span>
            <input
              type="text"
              placeholder="Job title, keywords, or company..."
              value={searchInput}
              onChange={handleSearchInputChange}
              className="jl-search-input"
            />
          </div>
          <button type="submit" className="jl-search-btn">
            <span className="material-symbols-outlined">search</span>
            Search
          </button>
        </form>
      </div>

      {/* Filters */}
      <div className="jl-filters">
        <div className="jl-filter-group">
          {/* <span className="jl-filter-label">Type</span> */}
          <select style={{width:'120px'}} name="jobType" value={filters.jobType} onChange={handleFilterChange} className="jl-filter-select jl-filter-drop">
            <option value="">All Types</option>
            {jobTypes.map((type) => (
              <option key={type._id} value={type.name}>{type.name}</option>
            ))}
          </select>
        </div>
        {/* <div className="jl-filter-divider"></div> */}
        <div className="jl-filter-group">
          {/* <span className="jl-filter-label">Mode</span> */}
          <select style={{width:'120px'}} name="workMode" value={filters.workMode} onChange={handleFilterChange} className="jl-filter-select jl-filter-drop">
            <option value="">All Modes</option>
            <option value="onsite">Onsite</option>
            <option value="remote">Remote</option>
            <option value="hybrid">Hybrid</option>
          </select>
        </div>
        {/* <div className="jl-filter-divider"></div> */}
        <div className="jl-filter-group">
          {/* <span className="jl-filter-label">Location</span> */}
          <input
            type="text"
            placeholder="City or country..."
            value={locationInput}
            onChange={handleLocationInputChange}
            onBlur={handleLocationBlur}
            onKeyPress={handleLocationKeyPress}
            className="jl-filter-input"
            style={{width:'350px'}}
          />
        </div>
        {(filters.search || filters.jobType || filters.workMode || filters.location) && (
          <>
            {/* <div className="jl-filter-divider"></div> */}
            <button onClick={clearFilters} className="jl-clear-btn">
              <span className="material-symbols-outlined">close</span>
              Clear
            </button>
          </>
        )}
      </div>

      {/* Results bar */}
      <div className="jl-results-bar">
        <p className="jl-results-count"><strong>{pagination.total || 0}</strong> jobs found</p>
      </div>

      {/* Grid */}
      <div className="jl-grid">
        {jobs.length === 0 ? (
          <div className="jl-empty">
            <span className="material-symbols-outlined">work_off</span>
            <h3>No jobs found</h3>
            <p>Try adjusting your filters or search terms</p>
          </div>
        ) : (
          jobs.map((job) => (
            <Link to={`/jobs/${job._id}`} key={job._id} className="jl-card">
              <div className="jl-card-top">
                <div className="jl-card-company-logo">
                  {job.company?.logo
                    ? <img src={job.company.logo} alt={job.company.name} />
                    : (job.title?.charAt(0) || 'J')}
                </div>
                <div className="jl-card-info">
                  <h3 className="jl-card-title">{job.title}</h3>
                  <p className="jl-card-company">{job.company?.name}</p>
                </div>
                <div className="jl-card-badges">
                  {job.featured && <span className="jl-badge-featured">Featured</span>}
                  {job.urgent && <span className="jl-badge-urgent">Urgent</span>}
                </div>

                <div className="jl-card-footer">
                  <span className="jl-card-applicants"></span>
                  <span className="jl-card-time">{getTimeAgo(job.createdAt)}</span>
                </div>

              </div>

              <div className="jl-card-meta">
                {job.jobType?.name && (
                  <span className="jl-meta-item">
                    <span className="material-symbols-outlined">work</span>
                    {job.jobType.name}
                  </span>
                )}
                {formatLocation(job.location) && (
                  <span className="jl-meta-item">
                    <span className="material-symbols-outlined">location_on</span>
                    {formatLocation(job.location)}
                  </span>
                )}
                <span className="jl-meta-item">
                  <span className="material-symbols-outlined">currency_rupee</span>
                  {formatSalary(job)}
                </span>
                <span className="jl-meta-item">
                  <span className="material-symbols-outlined">business_center</span>
                  {job.workMode}
                </span>
              </div>

              {job.description && (
                <p className="jl-card-desc">{job.description.substring(0, 200)}</p>
              )}

              {job.skills && job.skills.length > 0 && (
                <div className="jl-card-skills">
                  {job.skills.slice(0, 4).map((skill, i) => (
                    <span key={i} className="jl-skill">{skill.name}</span>
                  ))}
                  {job.skills.length > 4 && <span className="jl-skill">+{job.skills.length - 4}</span>}
                </div>
              )}
            </Link>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="jl-pagination">
          <button className="jl-page-btn" onClick={() => handlePageChange(filters.page - 1)} disabled={filters.page === 1}>
            <span className="material-symbols-outlined">chevron_left</span> Prev
          </button>
          {[...Array(pagination.pages)].map((_, i) => {
            const p = i + 1;
            if (p === 1 || p === pagination.pages || (p >= filters.page - 1 && p <= filters.page + 1)) {
              return <button key={p} className={`jl-page-num${filters.page === p ? ' active' : ''}`} onClick={() => handlePageChange(p)}>{p}</button>;
            } else if (p === filters.page - 2 || p === filters.page + 2) {
              return <span key={p} className="jl-page-dots">…</span>;
            }
            return null;
          })}
          <button className="jl-page-btn" onClick={() => handlePageChange(filters.page + 1)} disabled={filters.page === pagination.pages}>
            Next <span className="material-symbols-outlined">chevron_right</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default JobList;
